import { useMemo, useState } from "react";
import { Legend, TimeChart } from "@mops/charts-v2";
import type { Series } from "@mops/charts-v2";
import "./Ops.css";
import { Button } from "./Button";
import { EmptyState } from "./Card";
import { Input, Select } from "./Field";
import { Chip, StatusPill } from "./Status";
import { DownChip, ModeText, runnerState } from "./Runner";
import { agoText, trackLabel } from "../chat/api";
import type { Session } from "../chat/api";
import type { Issue } from "../lib/tasks";
import type { RunnerRow } from "../lib/auth";
import { tx } from "../i18n";

/* ══ 관측 › 지금(t-in-port-obs-ops) ══
   pr.b3x.app 「지금」(#/obs/ops)의 다섯 판을 b3x-in 이 가진 값으로 옮겼다 — 토큰 사용량 · 잠긴 이슈 ·
   나루 · 세션 이력 · 이벤트 조회. 새 화면을 세우지 않고 「세션 진행」(#/session)에 흡수한다
   (docs/decisions/scope.md 「`#/ops` → 세션 진행에 흡수」).

   **안 옮긴 것**: 추정 비용 · 예산 바 · 계정 묶음 · 시도 번호. 단가표와 예산은 온라인 애드온의 판정이고
   (scope.md), 계정 · 시도는 b3x-in 세션 기록에 없다 — 지어내지 않는다(pr-port.md §11).

   **접기는 화면이 한다** — `GET /agent-sessions` 한 번을 묶음별로 더한다. 토큰이 null 인 세션은 「계측됨」에서
   빠지고 합계에도 안 들어간다(0 으로 세면 「안 썼다」로 읽힌다). */

export type OpsRange = "1d" | "7d" | "30d";
export const OPS_RANGES: OpsRange[] = ["1d", "7d", "30d"];
const RANGE_MS: Record<OpsRange, number> = { "1d": 86_400_000, "7d": 7 * 86_400_000, "30d": 30 * 86_400_000 };

export type UsageAxis = "track" | "complexity" | "model" | "vendor" | "runner" | "project";
export const USAGE_AXES: UsageAxis[] = ["track", "complexity", "model", "vendor", "runner", "project"];

/** 세션이 붙은 이슈에서 읽는 사실 — 프로젝트 · 복잡도. 이슈 목록에 없으면(지워짐 · 범위 밖) null. */
export type IssueFacts = (taskId: string) => { project: string | null; complexity: string | null } | null;

/** 기간 안 세션 — 시작 시각으로 가른다. 시작을 모르는 줄은 기간을 모르니 뺀다. */
export function inRange(rows: Session[], range: OpsRange, now = Date.now()): Session[] {
  const from = now - RANGE_MS[range];
  return rows.filter((s) => s.startedAt !== null && Date.parse(s.startedAt) >= from);
}

export type UsageRow = { key: string; sessions: number; measured: number; input: number; output: number; ms: number };

/** 끝난 세션의 소요(ms). 도는 세션 · 시각을 모르는 줄은 null — 0 이 아니다. */
export function spanMs(s: Session): number | null {
  if (!s.startedAt || !s.endedAt) return null;
  return Math.max(0, Date.parse(s.endedAt) - Date.parse(s.startedAt));
}

/** 묶음별 합. 모르는 묶음 값(모델 · 벤더 · 프로젝트 null)은 한 줄 「모름」으로 모은다 — 버리지 않는다. */
function axisValue(s: Session, axis: UsageAxis, facts: IssueFacts): string | null {
  if (axis === "track") return s.track;
  if (axis === "complexity") return facts(s.taskId)?.complexity ?? null;
  if (axis === "model") return s.model ?? null;
  if (axis === "vendor") return s.vendor;
  if (axis === "runner") return s.runner;
  return facts(s.taskId)?.project ?? null;
}

/** 묶음 키의 사람 이름 — 트랙 · 복잡도만 바꾸고 나머지는 값 그대로다. */
export function axisLabel(axis: UsageAxis, key: string): string {
  if (axis === "track") return trackLabel(key || null);
  if (axis === "complexity" && (key === "deep" || key === "hard" || key === "normal" || key === "light")) return tx(`issue.complexity.${key}`);
  return key;
}

export function foldUsage(rows: Session[], axis: UsageAxis, facts: IssueFacts): UsageRow[] {
  const m = new Map<string, UsageRow>();
  for (const s of rows) {
    const raw = axisValue(s, axis, facts);
    const key = raw ?? "";
    const r = m.get(key) ?? { key, sessions: 0, measured: 0, input: 0, output: 0, ms: 0 };
    r.sessions += 1;
    if (s.inputTokens !== null || s.outputTokens !== null) {
      r.measured += 1;
      r.input += s.inputTokens ?? 0;
      r.output += s.outputTokens ?? 0;
    }
    r.ms += spanMs(s) ?? 0;
    m.set(key, r);
  }
  return [...m.values()].sort((a, b) => b.input + b.output - (a.input + a.output) || b.sessions - a.sessions);
}

export function sumUsage(rows: UsageRow[]): UsageRow {
  return rows.reduce((t, r) => ({ key: "", sessions: t.sessions + r.sessions, measured: t.measured + r.measured, input: t.input + r.input, output: t.output + r.output, ms: t.ms + r.ms }), {
    key: "",
    sessions: 0,
    measured: 0,
    input: 0,
    output: 0,
    ms: 0,
  });
}

const num = (n: number | null | undefined) => (n === null || n === undefined ? tx("ops.unknown") : n.toLocaleString("ko-KR"));

/** 「3시간 12분」 · 「8분」 · 「40초」. */
export function durText(ms: number | null): string {
  if (ms === null) return tx("ops.unknown");
  const sec = Math.round(ms / 1000);
  if (sec < 60) return tx("ops.dur.sec", { n: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return tx("ops.dur.min", { n: min });
  return tx("ops.dur.hour", { h: Math.floor(min / 60), m: min % 60 });
}

/** 판 머리 — 제목 · 한 줄 도움말(i 대신 제목 아래 캡션) · 오른쪽 손잡이. */
export function OpsSection({ id, title, help, end, children }: { id: string; title: string; help: string; end?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="ops-sec" aria-labelledby={`ops-${id}`}>
      <header className="ops-sec__head">
        <div className="ops-sec__titles">
          <h2 id={`ops-${id}`} className="ops-sec__title">
            {title}
          </h2>
          <p className="caption">{help}</p>
        </div>
        {end ? <div className="ops-sec__end">{end}</div> : null}
      </header>
      {children}
    </section>
  );
}

/* ── 1. 토큰 사용량 ── */
export function UsagePanel({ rows, axis, onAxis, capped }: { rows: UsageRow[]; axis: UsageAxis; onAxis: (a: UsageAxis) => void; capped: boolean }) {
  const t = sumUsage(rows);
  return (
    <>
      <div className="b-tabs ops-tabs" role="tablist" aria-label={tx("ops.usage.axis")}>
        {USAGE_AXES.map((a) => (
          <button key={a} type="button" role="tab" aria-selected={axis === a} className={`b-tab${axis === a ? " is-on" : ""}`} onClick={() => onAxis(a)}>
            {tx(`ops.usage.by.${a}`)}
          </button>
        ))}
      </div>
      <div className="ops-stats">
        {(
          [
            ["input", num(t.input)],
            ["output", num(t.output)],
            ["sessions", tx("ops.usage.measuredOf", { m: t.measured, n: t.sessions })],
            ["dur", durText(t.ms)],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="b-stat">
            <span className="caption">{tx(`ops.usage.stat.${k}`)}</span>
            <p className="b-stat-value">{v}</p>
          </div>
        ))}
      </div>
      {capped ? <p className="caption ops-note">▲ {tx("ops.usage.capped")}</p> : null}
      {rows.length ? (
        <div className="b-table-wrap">
          <table className="b-table">
            <thead>
              <tr>
                <th>{tx(`ops.usage.by.${axis}`)}</th>
                <th className="is-end">{tx("ops.usage.col.sessions")}</th>
                <th className="is-end">{tx("ops.usage.col.measured")}</th>
                <th className="is-end">{tx("ops.usage.stat.input")}</th>
                <th className="is-end">{tx("ops.usage.stat.output")}</th>
                <th className="is-end">{tx("ops.usage.stat.dur")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="is-primary">{r.key ? axisLabel(axis, r.key) : <span className="muted">{tx("ops.unknown")}</span>}</td>
                  <td className="is-end">{r.sessions}</td>
                  <td className="is-end">{r.measured}</td>
                  <td className="is-end">{r.measured ? num(r.input) : tx("ops.unknown")}</td>
                  <td className="is-end">{r.measured ? num(r.output) : tx("ops.unknown")}</td>
                  <td className="is-end">{durText(r.ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState compact title={tx("ops.usage.empty")} desc={tx("ops.usage.emptyDesc")} />
      )}
    </>
  );
}

/* ── 2. 잠긴 이슈 ── */
/** 담당이 박혀 있는데 도는 세션이 없는 열린 이슈 — 나루가 안 집는다(점유는 `claim` 의 조건부 UPDATE 하나). */
export function lockedIssues(rows: Issue[]): Issue[] {
  return rows.filter((t) => t.assignee && !t.openSession && t.status !== "done" && t.status !== "cancelled");
}

export function LockedTable({ rows, busyId, onUnlock }: { rows: Issue[]; busyId: string | null; onUnlock?: (id: string) => void }) {
  if (!rows.length) return <EmptyState compact icon="check" title={tx("ops.locked.empty")} desc={tx("ops.locked.emptyDesc")} />;
  return (
    <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th>{tx("ops.col.issue")}</th>
            <th>{tx("ops.col.status")}</th>
            <th>{tx("ops.locked.col.assignee")}</th>
            <th>{tx("ops.locked.col.kind")}</th>
            <th className="is-end">{tx("ops.col.action")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id}>
              <td className="is-primary ops-cell--title">
                <a href={`#/issue?id=${encodeURIComponent(t.id)}`}>{t.title}</a> <code className="muted">{t.id}</code>
              </td>
              <td>
                <StatusPill status={t.status} />
              </td>
              <td>{t.assignee}</td>
              <td>
                {/* 표식은 글자와 함께 — 색만으로 가르지 않는다. */}
                <Chip tone={t.assigneeKind === "held" ? "warning" : "muted"}>
                  {t.assigneeKind === "held" ? `◆ ${tx("ops.locked.held")}` : `■ ${tx("ops.locked.ticket")}`}
                </Chip>
              </td>
              <td className="is-end">
                {onUnlock ? (
                  <Button size="sm" loading={busyId === t.id} onClick={() => onUnlock(t.id)}>
                    {tx("ops.locked.unlock")}
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── 3. 나루 ── */
export function NaruTable({ rows, liveOnly, now = Date.now() }: { rows: RunnerRow[]; liveOnly: boolean; now?: number }) {
  const shown = liveOnly ? rows.filter((r) => runnerState(r) === "live") : rows;
  if (!shown.length) return <EmptyState compact icon="server" title={tx(liveOnly ? "ops.naru.emptyLive" : "ops.naru.empty")} desc={tx("ops.naru.emptyDesc")} />;
  return (
    <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th>{tx("ops.naru.col.name")}</th>
            <th>{tx("ops.naru.col.state")}</th>
            <th>{tx("ops.naru.col.mode")}</th>
            <th>{tx("ops.naru.col.vendors")}</th>
            <th>{tx("ops.naru.col.version")}</th>
            <th>{tx("ops.naru.col.first")}</th>
            <th>{tx("ops.naru.col.seen")}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => {
            const s = runnerState(r);
            return (
              <tr key={r.name}>
                <td className="is-primary">{r.name}</td>
                <td>
                  {s === "live" ? (
                    <Chip tone="success">● {tx("runners.state.live")}</Chip>
                  ) : s === "down" && r.downSince ? (
                    <DownChip since={r.downSince} now={now} />
                  ) : (
                    <Chip>{s === "down" ? `○ ${tx("runners.state.down")}` : `◌ ${tx("runners.state.never")}`}</Chip>
                  )}
                </td>
                <td>
                  <ModeText mode={r.presence?.mode} />
                </td>
                <td>{r.presence ? r.presence.vendors.join(", ") || tx("ops.none") : <span className="muted">{tx("ops.unknown")}</span>}</td>
                <td className="is-mono">{r.presence?.version ?? <span className="muted">{tx("ops.unknown")}</span>}</td>
                <td>{r.createdAt ? agoText(r.createdAt, now) : <span className="muted">{tx("ops.unknown")}</span>}</td>
                <td>{s === "live" && r.presence ? agoText(r.presence.since, now) : r.lastSeenAt ? agoText(r.lastSeenAt, now) : <span className="muted">{tx("ops.unknown")}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── 4. 세션 이력 ── */
const PAGE = 30;

export function SessionTable({ rows, now = Date.now(), onLog }: { rows: Session[]; now?: number; onLog: (s: Session) => void }) {
  const [shown, setShown] = useState(PAGE);
  if (!rows.length) return <EmptyState compact title={tx("ops.sess.empty")} desc={tx("ops.sess.emptyDesc")} />;
  return (
    <>
      <div className="b-table-wrap">
        <table className="b-table">
          <thead>
            <tr>
              <th>{tx("ops.col.issue")}</th>
              <th>{tx("ops.sess.col.track")}</th>
              <th>{tx("ops.sess.col.naru")}</th>
              <th>{tx("ops.usage.by.vendor")}</th>
              <th>{tx("ops.usage.by.model")}</th>
              <th>{tx("ops.sess.col.start")}</th>
              <th className="is-end">{tx("ops.sess.col.tokens")}</th>
              <th className="is-end">{tx("ops.usage.stat.dur")}</th>
              <th>{tx("ops.sess.col.end")}</th>
              <th className="is-end">{tx("ops.sess.col.log")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((s) => {
              const tok = s.inputTokens === null && s.outputTokens === null ? null : (s.inputTokens ?? 0) + (s.outputTokens ?? 0);
              return (
                <tr key={s.id}>
                  <td className="is-primary ops-cell--title">
                    <a href={`#/issue?id=${encodeURIComponent(s.taskId)}`}>{s.taskTitle ?? s.taskId}</a>
                  </td>
                  <td>{trackLabel(s.track)}</td>
                  <td>{s.runner}</td>
                  <td>{s.vendor ?? <span className="muted">{tx("ops.unknown")}</span>}</td>
                  <td className="is-mono">{s.model ?? <span className="muted">{tx("ops.unknown")}</span>}</td>
                  <td>{s.startedAt ? agoText(s.startedAt, now) : tx("ops.unknown")}</td>
                  <td className="is-end">{num(tok)}</td>
                  <td className="is-end">{s.endedAt ? durText(spanMs(s)) : tx("ops.sess.growing")}</td>
                  <td>
                    {s.endedAt === null ? (
                      <Chip tone="info">● {tx("ops.sess.running")}</Chip>
                    ) : (
                      <Chip tone={s.outcome === "ok" ? "success" : s.outcome ? "error" : "muted"}>{s.outcome ?? tx("ops.unknown")}</Chip>
                    )}
                  </td>
                  <td className="is-end">
                    <Button size="sm" variant="ghost" onClick={() => onLog(s)}>
                      {tx("ops.sess.open")}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > shown ? (
        <div className="ops-more">
          <Button size="md" onClick={() => setShown((n) => n + PAGE)}>
            {tx("ops.more", { n: rows.length - shown })}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** 이슈 id 좁히기 — Enter · 칸을 떠날 때 적용한다(글자마다 다시 읽지 않는다). */
export function TaskIdFilter({ value, onApply }: { value: string; onApply: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <Input
      size="md"
      type="search"
      className="ops-task"
      value={draft}
      placeholder={tx("ops.taskPh")}
      aria-label={tx("ops.task")}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft.trim() !== value && onApply(draft.trim())}
      onKeyDown={(e) => e.key === "Enter" && onApply(draft.trim())}
    />
  );
}

/* ── 5. 이벤트 조회 ── */
/** `GET /events` 한 줄(`crates/api/src/events.rs`). */
export type OpsEvent = { taskId: string; taskTitle: string; kind: string; actor: string; summary: string; from: string | null; to: string | null; at: string };

/** 발자국 갈래 — `events.rs` 의 TIMELINE 이 내는 값. 모르는 값은 그대로 그린다. */
export const EVENT_KINDS = ["status", "link", "message", "receive", "session-open", "session-close"] as const;

/** 갈래의 사람 이름 — 겉창의 발자국 이름(`chat.event.*`)을 그대로 쓴다. 모르는 값은 그대로 그린다. */
export function kindText(k: string): string {
  if (k === "status") return tx("ops.ev.status");
  if (k === "link") return tx("chat.event.link");
  if (k === "message") return tx("chat.event.message");
  if (k === "receive") return tx("chat.event.receive");
  if (k === "session-open") return tx("chat.event.sessionOpen");
  if (k === "session-close") return tx("chat.event.sessionClose");
  return k;
}

export function EventKindSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select size="md" value={value} aria-label={tx("ops.ev.kind")} onChange={(e) => onChange(e.target.value)}>
      <option value="">{tx("ops.ev.kindAll")}</option>
      {EVENT_KINDS.map((k) => (
        <option key={k} value={k}>
          {kindText(k)}
        </option>
      ))}
    </Select>
  );
}

export function EventTable({ rows, now = Date.now() }: { rows: OpsEvent[]; now?: number }) {
  if (!rows.length) return <EmptyState compact title={tx("ops.ev.empty")} desc={tx("ops.ev.emptyDesc")} />;
  return (
    <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th>{tx("ops.ev.col.at")}</th>
            <th>{tx("ops.ev.kind")}</th>
            <th>{tx("ops.col.issue")}</th>
            <th>{tx("ops.ev.col.msg")}</th>
            <th>{tx("ops.ev.col.actor")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e, i) => (
            <tr key={`${e.at}-${i}`}>
              <td title={e.at}>{agoText(e.at, now)}</td>
              <td>{kindText(e.kind)}</td>
              <td className="is-primary ops-cell--title">
                <a href={`#/issue?id=${encodeURIComponent(e.taskId)}`}>{e.taskTitle}</a>
              </td>
              <td className="ops-cell--msg">{e.kind === "status" ? `${e.from ?? "—"} → ${e.to ?? "—"}` : e.summary}</td>
              <td>{e.actor}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── 사용량 분석(t-in-port-obs-usage) ──
   pr.b3x.app 「관측 › 사용량」의 「어디에 얼마나 썼나 · 이슈마다 단계별로 · 무엇을 줄일까」를 같은 화면에 이었다.
   데이터는 위 사용량 판과 **같은** 세션 목록 하나다 — 두 판이 다른 수를 말하지 않게. */

/** 단계 띠 — 기획 · 설계 · 구현 · 리뷰 + 트랙 모름. 머지는 세션을 안 연다(토큰 0)라 따로 칸을 두지 않고 「모름」에 들어가지 않게 그대로 뺀다. */
export const STAGE_COLS = ["plan", "design", "run", "review", ""] as const;
const STAGE_COLOR: Record<string, string> = { plan: "--chart-4", design: "--chart-6", run: "--chart-1", review: "--chart-2", "": "--chart-9" };
const stageOf = (s: Session) => (s.track && (STAGE_COLS as readonly string[]).includes(s.track) ? s.track : s.track === "merge" ? null : "");
const tokOf = (s: Session) => (s.inputTokens === null && s.outputTokens === null ? null : (s.inputTokens ?? 0) + (s.outputTokens ?? 0));

/** 칸 폭 — 24시간은 1시간, 그보다 길면 하루. */
export const bucketOf = (range: OpsRange) => (range === "1d" ? 3_600_000 : 86_400_000);

/** 단계별 누적 막대 자료. 세션은 있는데 아무도 토큰을 못 밝힌 칸은 **null(모름)**, 세션이 없는 칸은 0 이다. */
export function stageSeries(rows: Session[], range: OpsRange, now = Date.now()) {
  const b = bucketOf(range);
  const end = Math.floor(now / b) * b;
  const n = Math.round(RANGE_MS[range] / b);
  const x = Array.from({ length: n }, (_, i) => end - (n - 1 - i) * b);
  const data: Record<string, Array<number | null>> = {};
  const seen: Record<string, boolean[]> = {};
  for (const k of STAGE_COLS) {
    data[k || "none"] = x.map(() => 0);
    seen[k || "none"] = x.map(() => false);
  }
  for (const s of rows) {
    const st = stageOf(s);
    if (st === null || !s.startedAt) continue;
    const i = Math.floor((Date.parse(s.startedAt) - x[0]) / b);
    if (i < 0 || i >= n) continue;
    const key = st || "none";
    const t = tokOf(s);
    if (t !== null) {
      data[key][i] = (data[key][i] ?? 0) + t;
      seen[key][i] = true;
    } else if (!seen[key][i]) data[key][i] = null;
  }
  return { x, ...data } as { x: number[] } & Record<string, Array<number | null>>;
}

export function StageChart({ rows, range, now }: { rows: Session[]; range: OpsRange; now?: number }) {
  const data = useMemo(() => stageSeries(rows, range, now), [rows, range, now]);
  const series: Series[] = STAGE_COLS.map((k) => ({ key: k || "none", label: k ? trackLabel(k) : tx("chat.trackUnknown"), color: STAGE_COLOR[k], kind: "bar", stack: "tok" }));
  const fmt = (v: number) => (v >= 1_000_000 ? `${Math.round(v / 100_000) / 10}M` : v >= 1000 ? `${Math.round(v / 1000)}k` : String(v));
  return (
    <div className="ops-chart">
      <Legend items={series.map((x) => ({ key: x.key, label: x.label, color: x.color }))} />
      <TimeChart data={data} series={series} bucketMs={bucketOf(range)} height={180} yAxisSize={48} y={{ min: 0, format: fmt }} timeZone="Asia/Seoul" ariaLabel={tx("ops.ana.chart")} />
    </div>
  );
}

export type IssueStageRow = { taskId: string; title: string; cells: Record<string, { n: number; tok: number | null }>; total: number | null; sessions: number };

/** 이슈 × 단계. 칸마다 세션 수와 토큰(계측된 것만 더함, 하나도 못 읽었으면 null). 합계 큰 순. */
export function foldIssueStages(rows: Session[]): IssueStageRow[] {
  const m = new Map<string, IssueStageRow>();
  for (const s of rows) {
    const st = stageOf(s);
    if (st === null) continue;
    const r = m.get(s.taskId) ?? { taskId: s.taskId, title: s.taskTitle ?? s.taskId, cells: {}, total: null, sessions: 0 };
    const c = r.cells[st] ?? { n: 0, tok: null };
    c.n += 1;
    const t = tokOf(s);
    if (t !== null) {
      c.tok = (c.tok ?? 0) + t;
      r.total = (r.total ?? 0) + t;
    }
    r.cells[st] = c;
    r.sessions += 1;
    m.set(s.taskId, r);
  }
  return [...m.values()].sort((a, b) => (b.total ?? -1) - (a.total ?? -1) || b.sessions - a.sessions);
}

export function IssueStageTable({ rows }: { rows: IssueStageRow[] }) {
  const [shown, setShown] = useState(PAGE);
  if (!rows.length) return <EmptyState compact title={tx("ops.usage.empty")} desc={tx("ops.usage.emptyDesc")} />;
  const cell = (c: { n: number; tok: number | null } | undefined) =>
    !c ? <span className="muted">—</span> : <span title={tx("ops.ana.cellTip", { n: c.n })}>{num(c.tok)}</span>;
  return (
    <>
      <div className="b-table-wrap">
        <table className="b-table">
          <thead>
            <tr>
              <th>{tx("ops.col.issue")}</th>
              {STAGE_COLS.map((k) => (
                <th key={k || "none"} className="is-end">
                  {k ? trackLabel(k) : tx("chat.trackUnknown")}
                </th>
              ))}
              <th className="is-end">{tx("ops.ana.total")}</th>
              <th className="is-end">{tx("ops.usage.col.sessions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((r) => (
              <tr key={r.taskId}>
                <td className="is-primary ops-cell--title">
                  <a href={`#/issue?id=${encodeURIComponent(r.taskId)}`}>{r.title}</a>
                </td>
                {STAGE_COLS.map((k) => (
                  <td key={k || "none"} className="is-end">
                    {cell(r.cells[k])}
                  </td>
                ))}
                <td className="is-end">{num(r.total)}</td>
                <td className="is-end">{r.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown ? (
        <div className="ops-more">
          <Button size="md" onClick={() => setShown((n) => n + PAGE)}>
            {tx("ops.more", { n: rows.length - shown })}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** 줄일 거리 — **판정이 아니라 사실**만 꺼낸다(끝내지 못한 세션이 쓴 토큰 · 못 읽은 세션 · 가장 많이 쓴 이슈의 몫).
 *  「이렇게 해라」를 지어내지 않고, 사람이 다음에 열어 볼 자리를 가리킨다. 해당이 없으면 그 줄은 안 낸다. */
export type SaveHint = { key: "failed" | "unmeasured" | "top"; text: string; href?: string };

export function saveHints(rows: Session[], issues: IssueStageRow[]): SaveHint[] {
  const out: SaveHint[] = [];
  const failed = rows.filter((s) => s.endedAt !== null && s.outcome !== null && s.outcome !== "ok");
  const failedMeasured = failed.filter((s) => tokOf(s) !== null);
  const failedTok = failedMeasured.reduce((a, s) => a + (tokOf(s) ?? 0), 0);
  // 하나도 못 읽었으면 「0 을 썼다」가 아니라 「모름」이다.
  if (failed.length)
    out.push({
      key: "failed",
      text: failedMeasured.length
        ? tx("ops.ana.hint.failed", { n: failed.length, tok: failedTok.toLocaleString("ko-KR") })
        : tx("ops.ana.hint.failedUnknown", { n: failed.length }),
    });
  const unmeasured = rows.filter((s) => s.endedAt !== null && tokOf(s) === null).length;
  if (unmeasured) out.push({ key: "unmeasured", text: tx("ops.ana.hint.unmeasured", { n: unmeasured }) });
  const all = issues.reduce((a, r) => a + (r.total ?? 0), 0);
  const top = issues[0];
  if (top && top.total && all > 0 && issues.length > 1)
    out.push({ key: "top", text: tx("ops.ana.hint.top", { title: top.title, pct: Math.round((top.total / all) * 100) }), href: `#/issue?id=${encodeURIComponent(top.taskId)}` });
  return out;
}

export function SaveHints({ hints }: { hints: SaveHint[] }) {
  if (!hints.length) return <EmptyState compact icon="check" title={tx("ops.ana.hintNone")} desc={tx("ops.ana.hintNoneDesc")} />;
  return (
    <ul className="ops-hints">
      {hints.map((h) => (
        <li key={h.key}>
          <span aria-hidden="true">▲</span> <span>{h.text}</span>
          {h.href ? (
            <>
              {" "}
              <a href={h.href}>{tx("ops.ana.open")} →</a>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
